import React from 'react';
import { Empty, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { InstallSourceReport } from '@/types/installSource';
import { formatMoney } from '@utils/formatMoney';
import { INSTALL_SOURCE_NOTE, type InstallSourceTableRow, toTableRows } from './installSourceRows';

const { Text } = Typography;

const pct = (rate: number) => `${(rate * 100).toFixed(1)}%`;

const inr = (amount: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const columns: ColumnsType<InstallSourceTableRow> = [
  { title: 'Source', dataIndex: 'label', key: 'label' },
  {
    title: 'New players',
    dataIndex: 'users',
    key: 'users',
    align: 'right',
    render: (n: number) => n.toLocaleString('en-IN'),
  },
  {
    title: 'Share',
    dataIndex: 'share',
    key: 'share',
    align: 'right',
    render: (share: number) => pct(share),
  },
  {
    title: 'FTUE completed',
    key: 'ftue',
    align: 'right',
    render: (_: unknown, row) => (
      <Tooltip
        title={`${row.ftueCompleted.toLocaleString('en-IN')} of ${row.users.toLocaleString('en-IN')}`}
      >
        {pct(row.ftueRate)}
      </Tooltip>
    ),
  },
  {
    title: 'D1 return',
    key: 'd1',
    align: 'right',
    render: (_: unknown, row) =>
      typeof row.d1Rate === 'number' ? (
        <Tooltip
          title={`${row.d1Returned.toLocaleString('en-IN')} of ${row.d1Eligible.toLocaleString('en-IN')} whose day 1 has ended`}
        >
          {pct(row.d1Rate)}
        </Tooltip>
      ) : (
        <Tooltip title="Nobody in this row has finished their day 1 yet">
          <Text type="secondary">—</Text>
        </Tooltip>
      ),
  },
  {
    title: 'Payers',
    dataIndex: 'payers',
    key: 'payers',
    align: 'right',
    render: (n: number) => n.toLocaleString('en-IN'),
  },
  {
    title: 'Revenue',
    key: 'revenue',
    align: 'right',
    render: (_: unknown, row) => {
      const others = Object.entries(row.revenue).filter(([code]) => code !== 'INR');
      const main = row.revenue.INR !== undefined ? inr(row.revenue.INR) : '—';
      return others.length > 0 ? (
        <Tooltip title={others.map(([code, amount]) => formatMoney(amount, code)).join(' + ')}>
          {main} +
        </Tooltip>
      ) : (
        main
      );
    },
  },
];

/** Install source of the players who joined in the chosen period (Players page summary panel). */
export const InstallSourceTable: React.FC<{ report: InstallSourceReport | null }> = ({
  report,
}) => {
  if (!report) {
    return <Empty description="Install source unavailable" />;
  }
  return (
    <>
      <Table<InstallSourceTableRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={toTableRows(report)}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        {INSTALL_SOURCE_NOTE}
      </Text>
    </>
  );
};
